import React from 'react'
import { Icons } from '@pankod/refine-antd'
import { useCustomMutation } from '@pankod/refine-core'

import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import UmpanBalikProgramDrawer, {
  useUmpanBalikProgramDrawer,
} from '../../../../program-setup/blocks/umpan-balik-program/UmpanBalikProgramDrawer'
import { FINALIZATION_CONFIG_STAGE } from '../../../../types'
import MenuItemPopconfirm from '../../MenuItemPopconfirm'
import { Highlighter } from '../../utils'
import { StatusTag } from '../StatusTag'

import type { TCommonError } from 'src/interfaces/common'
import type {
  TConfigurationSection,
  TConfigurationSectionContext,
} from '../types'

type TFinalizeState = 'FINALIZE_CONFIRMED' | 'FINALIZE_CANCELLED'

export const useFeedbackConfiguration = ({
  closeMenu,
  invalidateProgramDetail,
  program,
  programId,
  runAndClose,
  search,
}: TConfigurationSectionContext): TConfigurationSection => {
  const { show: showUmpanBalikProgram, ...drawerProps } =
    useUmpanBalikProgramDrawer()
  const { mutateAsync: finalize } = useCustomMutation<
    Record<string, never>,
    TCommonError,
    { finalizeState: TFinalizeState }
  >()
  const isFinalized =
    program?.umpanBalikConfigStage === FINALIZATION_CONFIG_STAGE.FINALIZED

  const finalizeConfig = async (finalizeState: TFinalizeState) => {
    await finalize(
      {
        method: 'post',
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${programId}/umpan-balik/finalize-config`,
        values: { finalizeState },
        successNotification: {
          message: `Status Umpan Balik telah diubah menjadi ${finalizeState === 'FINALIZE_CONFIRMED' ? 'final' : 'draft'}`,
          type: 'success',
          description: 'Sukses',
        },
        errorNotification: (error) =>
          showErrorNotification(
            error,
            'There was a problem when finalizing Umpan Balik',
          ),
      },
      { onSuccess: invalidateProgramDetail },
    )
    closeMenu()
  }

  return {
    menuItems: [
      {
        type: 'group',
        label: (
          <div className="flex justify-between gap-2">
            <Highlighter search={search} text="Umpan Balik Program" />
            <StatusTag active={isFinalized} />
          </div>
        ),
        key: 'Umpan Balik Program',
        children: [
          {
            icon: <Icons.CommentOutlined />,
            label: <Highlighter search={search} text="Kelola Umpan Balik" />,
            key: 'Kelola Umpan Balik',
            onClick: () =>
              runAndClose(() =>
                showUmpanBalikProgram(programId, program?.name!),
              ),
          },
          {
            icon: isFinalized ? (
              <Icons.UnlockOutlined />
            ) : (
              <Icons.LockOutlined />
            ),
            label: (
              <MenuItemPopconfirm
                title={
                  isFinalized
                    ? 'Apakah Anda yakin ingin mengubah status Umpan Balik Program menjadi draft?'
                    : 'Apakah Anda yakin ingin melakukan finalisasi Umpan Balik Program?'
                }
                onConfirm={() =>
                  finalizeConfig(
                    isFinalized ? 'FINALIZE_CANCELLED' : 'FINALIZE_CONFIRMED',
                  )
                }
                placement="right"
                okText={isFinalized ? 'Draft' : 'Finalisasi'}
                cancelText="Batal"
              >
                <Highlighter
                  search={search}
                  text={
                    isFinalized ? 'Draft Umpan Balik' : 'Finalisasi Umpan Balik'
                  }
                />
              </MenuItemPopconfirm>
            ),
            key: isFinalized ? 'Draft Umpan Balik' : 'Finalisasi Umpan Balik',
          },
        ],
      },
    ],
    overlays: (
      <UmpanBalikProgramDrawer
        {...drawerProps}
        isFinalized={isFinalized}
        programStatus={program?.status}
      />
    ),
  }
}
